import React, { useState } from "react"
import { useInView } from "react-intersection-observer"
import { useShallow } from "zustand/react/shallow"

// components
import ArmActions from "../../../components/ArmActions/ArmActions"
import DynamicField from "components/form-fields/DynamicField/DynamicField"
import CharCounter from "components/CharCounter/CharCounter"
import CellValidation from "../../../components/CellValidation/CellValidation"

// context
import useResultsTableArmsStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableArmsStore"
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"

// helpers
import { moveLeftHelper, moveRightHelper } from "../../helpers/helpers"

const Sas = ({ sasArr, sas, schema, fieldKey, rowIndex, colIndex, baselineData, setBaselineData }) => {
  const { ref: visibleRef, inView: inViewport } = useInView({
    /* Optional options */
    threshold: 0,
    triggerOnce: true,
  })

  const [showActions, setShowActions] = useState(false)

  const { readOnly, formView } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      formView: state.formView,
    }))
  )

  const { baselineSas, setBaselineSas } = useResultsTableArmsStore()

  const { tableView } = useResultsTableViewStore()

  const onChange = (e, arm) => {
    let tempData = { ...baselineData }

    const updateParticipants = (registry, key, group) => {
      tempData[registry].baseline_measures.forEach((bm) => {
        bm.rows.forEach((row) => {
          let catIndex = row.categories.findIndex((item) => item.id === "num-analyzed")
          if (catIndex !== -1) {
            let colIndex = row.categories[catIndex].values.findIndex((cat) => cat.arm_id === group.id)
            if (colIndex !== -1) {
              if (row.categories[catIndex].values[colIndex].participants === group.baseline_analysis_population) {
                row.categories[catIndex].values[colIndex].participants = e
              }
            }
          }
        })
      })
    }

    if (fieldKey === "baseline_analysis_population") {
      Object.keys(baselineData).forEach((registry) => {
        if (registry === "ctg") {
          let armIndex = tempData[registry].arms.findIndex((item) => item.id === arm.id)
          if (armIndex !== -1) {
            updateParticipants(registry, "arms", arm)
            tempData[registry].arms[armIndex][fieldKey] = e
          }
        } else if (registry === "eudract") {
          let armIndex = tempData[registry].arms.findIndex((item) => item.id === arm.id)
          if (armIndex !== -1) {
            updateParticipants(registry, "arms", arm)
            tempData[registry].arms[armIndex][fieldKey] = e
          } else {
            armIndex = tempData[registry]?.subject_analysis_sets?.findIndex((item) => item.id === arm.id)
            if (armIndex !== -1) {
              updateParticipants(registry, "subject_analysis_sets", arm)
              tempData[registry].subject_analysis_sets[armIndex][fieldKey] = e
            }
          }
        }
      })
    } else {
      Object.keys(baselineData).forEach((registry) => {
        if (registry === "ctg") {
          let armIndex = tempData[registry].arms.findIndex((item) => item.id === arm.id)
          if (armIndex !== -1) {
            tempData[registry].arms[armIndex][fieldKey] = e
          }
        } else if (registry === "eudract") {
          let setIndex = tempData[registry]?.subject_analysis_sets?.findIndex((item) => item.id === arm.id)
          if (setIndex !== -1) {
            tempData[registry].subject_analysis_sets[setIndex][fieldKey] = e
          }
        }
      })
    }

    let tempBaselineSas = [...baselineSas]
    let foundSasIndex = tempBaselineSas.findIndex((sas) => sas.id === "arm.id")
    if (foundSasIndex !== -1) {
      tempBaselineSas[foundSasIndex][fieldKey] = e
      setBaselineSas(tempBaselineSas)
    }

    setBaselineData(tempData)
  }

  const handleLeft = () => {
    let tempData = { ...baselineData }
    let foundSetIndex = tempData[tableView]?.subject_analysis_sets?.findIndex((item) => item.id === sas.id)
    if (foundSetIndex !== -1 && foundSetIndex !== 0) {
      moveLeftHelper(tempData, tableView, "subject_analysis_sets", foundSetIndex)
    }

    // move baseline measure values
    tempData[tableView].baseline_measures.forEach((bm) => {
      bm.rows.forEach((row) => {
        row.categories.forEach((category) => {
          let tempValues = [...category.values]
          let foundIndex = tempValues.findIndex((item) => item.arm_id === sas.id)

          if (foundIndex !== -1 && foundIndex !== 0) {
            let saved = tempValues[foundIndex - 1]
            tempValues[foundIndex - 1] = tempValues[foundIndex]
            tempValues[foundIndex] = saved
            category.values = [...tempValues]
          }
        })
      })
    })

    setBaselineData(tempData)
  }

  const handleRight = () => {
    let tempData = { ...baselineData }
    // tempData[tableView].subject_analysis_sets
    let foundSetIndex = tempData[tableView]?.subject_analysis_sets?.findIndex((item) => item.id === sas.id)
    if (foundSetIndex !== -1 && foundSetIndex !== tempData[tableView].arms.length - 1) {
      moveRightHelper(tempData, tableView, "subject_analysis_sets", foundSetIndex)
    }

    // move baseline measure values
    tempData[tableView].baseline_measures.forEach((bm) => {
      bm.rows.forEach((row) => {
        row.categories.forEach((category) => {
          let tempValues = [...category.values]
          let foundIndex = category.values.findIndex((item) => item.arm_id === sas.id)

          if (foundIndex !== -1 && foundIndex !== category.values.length - 1) {
            let saved = tempValues[foundIndex + 1]
            tempValues[foundIndex + 1] = tempValues[foundIndex]
            tempValues[foundIndex] = saved
            category.values = [...tempValues]
          }
        })
      })
    })

    setBaselineData(tempData)
  }

  const handleDelete = (sas, deleteAll) => {
    let tempData = { ...baselineData }
    Object.keys(tempData).forEach((registry) => {
      if (deleteAll === true || registry === tableView) {
        // delete sas
        let foundIndex = tempData[registry]?.arms?.findIndex((item) => item.id === sas.id)
        if (foundIndex !== -1 && foundIndex !== undefined) tempData[registry].arms.splice(foundIndex, 1)

        if (registry === "eudract") {
          foundIndex = tempData[registry]?.subject_analysis_sets?.findIndex((item) => item.id === sas.id)
          if (foundIndex !== -1 && foundIndex !== undefined)
            tempData[registry].subject_analysis_sets.splice(foundIndex, 1)
        }

        // delete baseline measure values
        tempData[registry]?.baseline_measures?.forEach((bm) => {
          bm.rows.forEach((row) => {
            row.categories.forEach((category) => {
              let tempValues = [...category.values]
              let foundIndex = category.values.findIndex((item) => item.arm_id === sas.id)

              if (foundIndex !== -1) {
                tempValues.splice(foundIndex, 1)
                category.values = [...tempValues]
              }
            })
          })
        })
      }
    })

    setBaselineData(tempData)
  }

  let isManualOverride = fieldKey === "title" || fieldKey === "description"

  const errorKey = `results_baseline_characteristics_baseline_characteristics_table_${tableView}_subject_analysis_sets_${
    colIndex + 1
  }_${fieldKey}`

  const armGeneralErrorKey = `results_baseline_characteristics_baseline_characteristics_table_${tableView}_subject_analysis_sets_${
    colIndex + 1
  }`

  return (
    <td
      ref={visibleRef}
      className="text-left subject-analysis-set"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {inViewport ? (
        <>
          <DynamicField
            type={schema.type}
            fieldProps={{
              style: { width: "100%" },
              placeholder: schema.title,
              options: schema.enum,
              disabled: isManualOverride,
              readOnly,
              value: sas[fieldKey],
              onChange: (e) => onChange(e, sas),
            }}
          />

          <div className="bc-arm-actions">
            {schema?.charCountEnabled && formView?.id !== "global" && !readOnly && (
              <div className="char-counter-container">
                <CharCounter
                  value={sas[fieldKey]}
                  maxChars={schema.maxChars}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <div style={{ padding: 10 }} />
      )}
      <ArmActions
        arms={sasArr}
        arm={sas}
        handleLeft={rowIndex === 0 ? handleLeft : null}
        handleRight={rowIndex === 0 ? handleRight : null}
        canDelete={rowIndex === 0 ? true : false}
        handleDeleteArm={(sas, deleteAll) => handleDelete(sas, deleteAll)}
        colIndex={colIndex}
        showActions={showActions}
        setShowActions={setShowActions}
        jpath={`bc-sas-${sas.id}-${fieldKey}`}
        source={"baseline-characteristics"}
        manualOverride={isManualOverride}
        type={"sas"}
      />
      <CellValidation
        table="baseline"
        errorKey={errorKey}
      />
      {rowIndex === 0 && (
        <CellValidation
          table="baseline"
          errorKey={armGeneralErrorKey}
        />
      )}
    </td>
  )
}

export default Sas
